import type { components } from '@feedfm/clips-services-api';
import CloseIcon from '@mui/icons-material/Close';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useStudioAuthenticatedContext } from '../../context/StudioContext';
import { useToast } from '../../context/ToastContext';
import { CollectionSortBy } from '../../util/collectionSort';
import { normalizeTrackKey, normalizeTrackText } from '../../util/normalizeTrackKey';
import { useFeatureFlags } from '../../util/useFeatureFlags';
import { FeedbackReasonDialog } from './FeedbackReasonDialog';
import { NotSoundReasonDialog } from './NotSoundReasonDialog';
import * as styles from './SimilarTracksModal.styles';
import { SoundReasonDialog } from './SoundReasonDialog';
import { createSingleClipCollection, TrackCard } from './TrackCard';
import {
  NOT_SOUND_SHORTCUTS,
  SOUND_SHORTCUTS,
  WHICH_SONG_SHORTCUTS,
  type FeedbackDialogState,
  type NotSoundReason,
  type NotSoundReasonDialogState,
  type SimilarTrack,
  type SoundReason,
  type SoundReasonDialogState,
  type VibeTracksModalState,
  type WhichSongDialogState,
  type WhichSongReason,
  type WhichSongSelection,
} from './types';
import { WhichSongDialog } from './WhichSongDialog';

// Centralized action types for feedback
export type FeedbackAction = 'thumbs_up' | 'thumbs_down' | 'nothing';

// Action types for displayed feedback (excludes 'nothing' since it's only used for submission)
export type DisplayedFeedbackAction = 'thumbs_up' | 'thumbs_down';

// Centralized action constants
export const FEEDBACK_ACTION = {
  THUMBS_UP: 'thumbs_up' as const,
  THUMBS_DOWN: 'thumbs_down' as const,
  NOTHING: 'nothing' as const,
} as const;

// Centralized search type options
export type SearchType = 'similar_track' | 'vibe_tag' | 'text_search';

// Centralized search type constants
export const SEARCH_TYPE = {
  SIMILAR_TRACK: 'similar_track' as const,
  VIBE_TAG: 'vibe_tag' as const,
  TEXT_SEARCH: 'text_search' as const,
} as const;

interface SessionContext {
  searchType: SearchType;
  sourceTrackEmbeddingId?: number;
  sourceTrackUrl?: string;
  vibeTagId?: number;
  vibeTagName?: string;
  textSearchQuery?: string;
  filtersApplied?: Record<string, unknown>;
  embeddingModel?: string;
}

// Convert SessionContext to API format (embeddingModel: string | null instead of string | undefined)
function convertSessionContextToApi(
  context: SessionContext | undefined | null,
): components['schemas']['FeedbackSessionContext'] | undefined {
  if (!context) return undefined;
  return {
    ...context,
    embeddingModel: context.embeddingModel ?? null,
    sourceTrackEmbeddingId: context.sourceTrackEmbeddingId ?? null,
    sourceTrackUrl: context.sourceTrackUrl ?? null,
    vibeTagId: context.vibeTagId ?? null,
    vibeTagName: context.vibeTagName ?? null,
    textSearchQuery: context.textSearchQuery ?? null,
    filtersApplied: context.filtersApplied ?? null,
  };
}

interface SimilarTracksModalProps {
  open: boolean;
  onClose: () => void;
  trackTitle: string;
  trackArtist: string;
  audioUrl?: string;
  vibeTagId?: number;
}

export const SimilarTracksModal = ({
  open,
  onClose,
  trackTitle,
  trackArtist,
  audioUrl,
  vibeTagId,
}: SimilarTracksModalProps) => {
  const { backendClient, playerState } = useStudioAuthenticatedContext();
  const { showToast } = useToast();
  const { isEnabled } = useFeatureFlags();
  const feedbackTestModeRef = useRef(isEnabled('feedbackTestMode'));
  
  // Update ref when feature flag changes
  useEffect(() => {
    feedbackTestModeRef.current = isEnabled('feedbackTestMode');
  }, [isEnabled]);

  const [similarTracks, setSimilarTracks] = useState<SimilarTrack[]>([]);
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(
    null,
  );
  const [seedSongTrackEmbeddingId, setSeedSongTrackEmbeddingId] = useState<
    number | null
  >(null);
  const [previousFeedback, setPreviousFeedback] = useState<
    Map<number, { action: DisplayedFeedbackAction; createdAt: Date }>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState<FeedbackDialogState>({
    open: false,
    trackId: 0,
    trackTitle: '',
  });
  const [vibeTracksModal, setVibeTracksModal] = useState<VibeTracksModalState>({
    open: false,
    vibeTagId: 0,
    vibeTagName: '',
  });
  const [notSoundReasonDialog, setNotSoundReasonDialog] =
    useState<NotSoundReasonDialogState>({
      open: false,
      otherText: '',
      showOtherInput: false,
    });
  const [soundReasonDialog, setSoundReasonDialog] =
    useState<SoundReasonDialogState>({
      open: false,
      otherText: '',
      showOtherInput: false,
    });
  const [whichSongDialog, setWhichSongDialog] = useState<WhichSongDialogState>({
    open: false,
    reason: null,
  });

  // Prevent concurrent requests
  const fetchingRef = useRef(false);

  const fetchSimilarTracks = useCallback(async () => {
    // Prevent concurrent requests
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      let response;
      let context: SessionContext;

      if (vibeTagId) {
        response = await backendClient.tracksByVibeTag({
          vibeTagId,
          limit: 10,
        });
        context = {
          searchType: SEARCH_TYPE.VIBE_TAG,
          vibeTagId,
          vibeTagName: trackTitle,
          embeddingModel: 'CLAP',
        };
      } else {
        response = await backendClient.similarTracks({
          audioUrl,
          title: trackTitle,
          displayArtistName: trackArtist,
          limit: 10,
          includeVibeTags: true,
        });
        context = {
          searchType: SEARCH_TYPE.SIMILAR_TRACK,
          sourceTrackUrl: audioUrl,
          embeddingModel: 'CLAP',
        };
      }

      setSessionContext(context);

      const tracksToFilter = vibeTagId
        ? response.similarTracks || []
        : (response.similarTracks || []).filter((track: SimilarTrack) => {
            if (audioUrl) {
              const originalUrl = audioUrl.startsWith('s3://')
                ? audioUrl
                : `s3://clips-audio-prod/${audioUrl}`;
              const normalizedOriginalUrl = originalUrl.trim();
              const normalizedTrackUrl = track.url.trim();
              if (
                normalizedTrackUrl === normalizedOriginalUrl ||
                normalizedTrackUrl === originalUrl ||
                track.url === normalizedOriginalUrl
              ) {
                return false;
              }
            }

            const normalizedTrackTitle = normalizeTrackText(track.title);
            const normalizedOriginalTitle = normalizeTrackText(trackTitle);
            const normalizedTrackArtist = normalizeTrackText(
              track.displayArtistName,
            );
            const normalizedOriginalArtist = normalizeTrackText(trackArtist);

            if (
              normalizedTrackTitle === normalizedOriginalTitle &&
              normalizedTrackArtist === normalizedOriginalArtist
            ) {
              return false;
            }

            return true;
          });

      // Deduplicate by title/artist
      const seen = new Set<string>();
      const uniqueTracks = tracksToFilter.filter((track: SimilarTrack) => {
        const key = normalizeTrackKey(track.title, track.displayArtistName);

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });

      const finalTracks = uniqueTracks.slice(0, 3);
      setSimilarTracks(finalTracks);

      // Store seed song track embedding ID if available
      // Handle both response types (similarTracks includes seedSongTrackEmbeddingId, tracksByVibeTag does not)
      let seedSongId: number | null = null;
      if (!vibeTagId && 'seedSongTrackEmbeddingId' in response) {
        seedSongId =
          (response as { seedSongTrackEmbeddingId?: number | null })
            .seedSongTrackEmbeddingId ?? null;
        if (seedSongId !== null) {
          setSeedSongTrackEmbeddingId(seedSongId);
          // Update sessionContext with sourceTrackEmbeddingId so feedback is stored correctly
          setSessionContext((prev) => {
            if (prev && prev.searchType === SEARCH_TYPE.SIMILAR_TRACK) {
              return {
                ...prev,
                sourceTrackEmbeddingId: seedSongId ?? undefined,
              };
            }
            return prev;
          });
        }
      }

      // Fetch previous feedback for these tracks
      if (finalTracks.length > 0) {
        try {
          const trackIds = finalTracks.map((t) => t.trackEmbeddingId);
          // Use ref to avoid dependency on isEnabled function
          // If we have seedSongTrackEmbeddingId, fetch feedback for seed -> recommendation pairs
          const feedbackResponse = await backendClient.getFeedbackForTracks({
            trackEmbeddingIds: trackIds,
            includeTest: feedbackTestModeRef.current,
            seedSongId: seedSongId ?? undefined,
          });

          const feedbackMap = new Map<
            number,
            { action: DisplayedFeedbackAction; createdAt: Date }
          >();
          if (feedbackResponse.feedback) {
            Object.entries(feedbackResponse.feedback).forEach(([id, fb]) => {
              // Type guard to ensure action and createdAt are present
              const feedbackItem = fb as {
                action?: 'thumbs_up' | 'thumbs_down';
                createdAt?: string;
              };
              if (
                feedbackItem?.action &&
                (feedbackItem.action === 'thumbs_up' ||
                  feedbackItem.action === 'thumbs_down') &&
                feedbackItem?.createdAt
              ) {
                feedbackMap.set(parseInt(id, 10), {
                  action: feedbackItem.action,
                  createdAt: new Date(feedbackItem.createdAt),
                });
              }
            });
          }
          setPreviousFeedback(feedbackMap);
        } catch (err) {
          // Silently fail - feedback fetching is optional
          console.warn('Failed to fetch previous feedback:', err);
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch similar tracks',
      );
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [audioUrl, trackTitle, trackArtist, vibeTagId, backendClient]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSimilarTracks([]);
    setError(null);
    setPreviousFeedback(new Map());
    setSeedSongTrackEmbeddingId(null);
    fetchSimilarTracks();
  }, [open, fetchSimilarTracks]);

  const handleClose = () => {
    setSimilarTracks([]);
    setError(null);
    onClose();
  };

  // Build recommendations array for feedback submission
  const buildRecommendationsForFeedback = useCallback(() => {
    return similarTracks.map((track, index) => ({
      trackEmbeddingId: track.trackEmbeddingId,
      trackTitle: track.title,
      trackArtist: track.displayArtistName,
      trackUrl: track.url,
      jobId: track.jobId,
      displayOrder: index + 1,
      similarityScore: track.similarity,
      distance: track.distance,
      sharedVibeTags: track.vibeTags?.map((tag) => ({
        vibeTagId: tag.vibeTagId,
        vibeTag: tag.vibeTag,
        track1Score: tag.track1Score,
        track2Score: tag.track2Score,
        sharedScore: tag.sharedScore,
      })),
    }));
  }, [similarTracks]);

  const submitFeedback = useCallback(
    async (
      reason: 'sound' | 'not_sound',
      notSoundReason?: string,
      soundReason?: string,
      whichSong?: 'original' | 'similar' | 'neither',
    ) => {
      try {
        const track = similarTracks.find(
          (t) => t.trackEmbeddingId === feedbackDialog.trackId,
        );

        const isTest = isEnabled('feedbackTestMode');
        
        // Ensure sessionContext includes sourceTrackEmbeddingId if we have seedSongTrackEmbeddingId
        const contextWithSeedId = seedSongTrackEmbeddingId && sessionContext
          ? {
              ...sessionContext,
              sourceTrackEmbeddingId: seedSongTrackEmbeddingId,
            }
          : sessionContext;
        
        await backendClient.submitSimilarTrackFeedback({
          action: FEEDBACK_ACTION.THUMBS_DOWN,
          trackEmbeddingId: feedbackDialog.trackId,
          trackTitle: feedbackDialog.trackTitle,
          trackArtist: track?.displayArtistName || '',
          originalTrackTitle: trackTitle,
          originalTrackArtist: trackArtist,
          reason,
          notSoundReason,
          soundReason,
          whichSong,
          isTest,
          sessionContext: convertSessionContextToApi(contextWithSeedId),
          allRecommendations: buildRecommendationsForFeedback(),
        });
        
        // Update local feedback state
        setPreviousFeedback((prev) => {
          const updated = new Map(prev);
          updated.set(feedbackDialog.trackId, {
            action: FEEDBACK_ACTION.THUMBS_DOWN,
            createdAt: new Date(),
          });
          return updated;
        });
        
        showToast(
          `Feedback submitted${isTest ? ' (test mode)' : ''}`,
          'success',
        );
        
        setFeedbackDialog({ open: false, trackId: 0, trackTitle: '' });
        setNotSoundReasonDialog({
          open: false,
          otherText: '',
          showOtherInput: false,
        });
        setSoundReasonDialog({
          open: false,
          otherText: '',
          showOtherInput: false,
        });
      } catch (err) {
        console.error('Failed to send thumbs down feedback:', err);
        showToast('Failed to submit feedback. Please try again.', 'error');
      }
    },
    [
      backendClient,
      feedbackDialog.trackId,
      feedbackDialog.trackTitle,
      similarTracks,
      trackTitle,
      trackArtist,
      sessionContext,
      seedSongTrackEmbeddingId,
      buildRecommendationsForFeedback,
      isEnabled,
      showToast,
    ],
  );

  const handleNotSoundReason = useCallback(
    (reason: NotSoundReason) => {
      if (reason === 'other') {
        setNotSoundReasonDialog({
          open: true,
          otherText: '',
          showOtherInput: true,
        });
        return;
      }

      const requiresWhichSong: WhichSongReason[] = [
        'holiday',
        'childrens',
        'wrong_language',
        'explicit',
        'devotional',
      ];

      if (requiresWhichSong.includes(reason as WhichSongReason)) {
        setNotSoundReasonDialog((prev) => ({ ...prev, open: false }));
        setWhichSongDialog({ open: true, reason: reason as WhichSongReason });
        return;
      }

      submitFeedback('not_sound', reason);
    },
    [submitFeedback],
  );

  const handleWhichSong = useCallback(
    (whichSong: WhichSongSelection) => {
      const reason = whichSongDialog.reason;
      if (!reason) return;

      setWhichSongDialog({ open: false, reason: null });
      // Pass whichSong as a separate parameter for cleaner data storage
      submitFeedback('not_sound', reason, undefined, whichSong);
    },
    [whichSongDialog.reason, submitFeedback],
  );

  const handleSoundReason = useCallback(
    (reason: SoundReason) => {
      if (reason === 'other') {
        setSoundReasonDialog({
          open: true,
          otherText: '',
          showOtherInput: true,
        });
        return;
      }
      submitFeedback('sound', undefined, reason);
    },
    [submitFeedback],
  );

  const handleThumbsUp = useCallback(async (track: SimilarTrack) => {
    try {
      const isTest = isEnabled('feedbackTestMode');
      const existingFeedback = previousFeedback.get(track.trackEmbeddingId);
      
      // If user already gave thumbs_up, send "nothing" to undo
      const action: FeedbackAction =
        existingFeedback?.action === FEEDBACK_ACTION.THUMBS_UP
          ? FEEDBACK_ACTION.NOTHING
          : FEEDBACK_ACTION.THUMBS_UP;
      
      // Ensure sessionContext includes sourceTrackEmbeddingId if we have seedSongTrackEmbeddingId
      const contextWithSeedId = seedSongTrackEmbeddingId && sessionContext
        ? {
            ...sessionContext,
            sourceTrackEmbeddingId: seedSongTrackEmbeddingId,
          }
        : sessionContext;
      
      await backendClient.submitSimilarTrackFeedback({
        action,
        trackEmbeddingId: track.trackEmbeddingId,
        trackTitle: track.title,
        trackArtist: track.displayArtistName,
        originalTrackTitle: trackTitle,
        originalTrackArtist: trackArtist,
        isTest,
        sessionContext: convertSessionContextToApi(contextWithSeedId),
        allRecommendations: buildRecommendationsForFeedback(),
      });
      
      // Update local feedback state
      setPreviousFeedback((prev) => {
        const updated = new Map(prev);
        if (action === FEEDBACK_ACTION.NOTHING) {
          // Remove from map to clear highlighting
          updated.delete(track.trackEmbeddingId);
        } else {
          updated.set(track.trackEmbeddingId, {
            action: FEEDBACK_ACTION.THUMBS_UP,
            createdAt: new Date(),
          });
        }
        return updated;
      });
      
      const message = action === FEEDBACK_ACTION.NOTHING 
        ? 'Feedback removed'
        : `Thanks for the feedback!${isTest ? ' (test mode)' : ''}`;
      showToast(message, 'success');
    } catch (err) {
      console.error('Failed to send thumbs up feedback:', err);
      showToast('Failed to submit feedback. Please try again.', 'error');
    }
  }, [
    previousFeedback,
    isEnabled,
    backendClient,
    trackTitle,
    trackArtist,
    sessionContext,
    seedSongTrackEmbeddingId,
    buildRecommendationsForFeedback,
    showToast,
  ]);

  const handleThumbsDown = useCallback(async (track: SimilarTrack) => {
    const existingFeedback = previousFeedback.get(track.trackEmbeddingId);
    
    // If user already gave thumbs_down, send "nothing" to undo
    if (existingFeedback?.action === FEEDBACK_ACTION.THUMBS_DOWN) {
      try {
        const isTest = isEnabled('feedbackTestMode');
        
        // Ensure sessionContext includes sourceTrackEmbeddingId if we have seedSongTrackEmbeddingId
        const contextWithSeedId = seedSongTrackEmbeddingId && sessionContext
          ? {
              ...sessionContext,
              sourceTrackEmbeddingId: seedSongTrackEmbeddingId,
            }
          : sessionContext;
        
        await backendClient.submitSimilarTrackFeedback({
          action: FEEDBACK_ACTION.NOTHING,
          trackEmbeddingId: track.trackEmbeddingId,
          trackTitle: track.title,
          trackArtist: track.displayArtistName,
          originalTrackTitle: trackTitle,
          originalTrackArtist: trackArtist,
          isTest,
          sessionContext: convertSessionContextToApi(contextWithSeedId),
          allRecommendations: buildRecommendationsForFeedback(),
        });
        
        // Remove from map to clear highlighting
        setPreviousFeedback((prev) => {
          const updated = new Map(prev);
          updated.delete(track.trackEmbeddingId);
          return updated;
        });
        
        showToast('Feedback removed', 'success');
      } catch (err) {
        console.error('Failed to send nothing feedback:', err);
        showToast('Failed to submit feedback. Please try again.', 'error');
      }
      return;
    }
    
    // Otherwise, open the feedback dialog
    setFeedbackDialog({
      open: true,
      trackId: track.trackEmbeddingId,
      trackTitle: track.title,
    });
  }, [
    previousFeedback,
    isEnabled,
    backendClient,
    trackTitle,
    trackArtist,
    sessionContext,
    seedSongTrackEmbeddingId,
    buildRecommendationsForFeedback,
    showToast,
  ]);

  const handleFeedbackReason = useCallback((reason: 'sound' | 'not_sound') => {
    if (reason === 'not_sound') {
      setFeedbackDialog((prev) => ({ ...prev, open: false }));
      setNotSoundReasonDialog({
        open: true,
        otherText: '',
        showOtherInput: false,
      });
      return;
    }

    setFeedbackDialog((prev) => ({ ...prev, open: false }));
    setSoundReasonDialog({
      open: true,
      otherText: '',
      showOtherInput: false,
    });
  }, []);

  const handleSoundOtherReasonSubmit = () => {
    const otherText = soundReasonDialog.otherText.trim();
    if (otherText) {
      submitFeedback('sound', undefined, `other: ${otherText}`);
    }
  };

  const handleOtherReasonSubmit = () => {
    const otherText = notSoundReasonDialog.otherText.trim();
    if (otherText) {
      submitFeedback('not_sound', `other: ${otherText}`);
    }
  };

  const handlePlaySourceTrack = () => {
    if (!audioUrl) return;

    // Use a stable ID for the source track based on the audioUrl
    const sourceClipId = Math.abs(
      audioUrl.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0),
    );
    const isSourcePlaying =
      playerState.isClipPlaying(sourceClipId) && playerState.playIntent;

    if (isSourcePlaying) {
      playerState.togglePlayback();
    } else {
      const sourceCollection: components['schemas']['Collection'] = {
        collectionId: sourceClipId,
        collectionName: trackTitle,
        description: `${trackTitle} by ${trackArtist}`,
        publishedOn: null,
        rating: 'unknown',
        explicit: false,
        clips: [
          {
            clipId: sourceClipId,
            trackId: 0,
            title: trackTitle,
            artist: trackArtist,
            album: '',
            duration: 0,
            preSignedUrl: audioUrl,
            preSignedUrlExpires: '',
            encoding: '',
            bitrate: 0,
            explicitness: 'unknown',
            createDate: new Date().toISOString(),
          },
        ],
      };
      playerState.clickCollection(sourceCollection, CollectionSortBy.NAME);
    }
  };

  const isSourceTrackPlaying = audioUrl
    ? playerState.isClipPlaying(
        Math.abs(
          audioUrl.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0),
        ),
      ) && playerState.playIntent
    : false;

  const handlePlayPause = (track: SimilarTrack) => {
    if (!track.preSignedUrl) return;

    const clipId = track.clipId ?? track.trackEmbeddingId;
    const isPlaying =
      clipId && playerState.isClipPlaying(clipId) && playerState.playIntent;

    if (isPlaying) {
      playerState.togglePlayback();
    } else {
      playerState.clickCollection(
        createSingleClipCollection(track),
        CollectionSortBy.NAME,
      );
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleShortcutKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      const key = event.key.toLowerCase();

      if (feedbackDialog.open) {
        if (key === 's') {
          event.preventDefault();
          handleFeedbackReason('sound');
          return;
        }
        if (key === 'n') {
          event.preventDefault();
          handleFeedbackReason('not_sound');
          return;
        }
      }

      if (notSoundReasonDialog.open && !notSoundReasonDialog.showOtherInput) {
        const shortcut =
          NOT_SOUND_SHORTCUTS[key as keyof typeof NOT_SOUND_SHORTCUTS];
        if (shortcut) {
          event.preventDefault();
          handleNotSoundReason(shortcut);
          return;
        }
      }

      if (soundReasonDialog.open && !soundReasonDialog.showOtherInput) {
        const shortcut = SOUND_SHORTCUTS[key as keyof typeof SOUND_SHORTCUTS];
        if (shortcut) {
          event.preventDefault();
          handleSoundReason(shortcut);
          return;
        }
      }

      if (whichSongDialog.open) {
        const shortcut =
          WHICH_SONG_SHORTCUTS[key as keyof typeof WHICH_SONG_SHORTCUTS];
        if (!shortcut) {
          return;
        }

        if (
          shortcut === 'neither' &&
          whichSongDialog.reason !== 'wrong_language'
        ) {
          return;
        }

        event.preventDefault();
        handleWhichSong(shortcut);
      }
    };

    window.addEventListener('keydown', handleShortcutKey);
    return () => {
      window.removeEventListener('keydown', handleShortcutKey);
    };
  }, [
    open,
    handleClose,
    feedbackDialog.open,
    notSoundReasonDialog.open,
    notSoundReasonDialog.showOtherInput,
    soundReasonDialog.open,
    soundReasonDialog.showOtherInput,
    whichSongDialog.open,
    whichSongDialog.reason,
    handleFeedbackReason,
    handleNotSoundReason,
    handleSoundReason,
    handleWhichSong,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box>
          <Typography variant="h6" component="span" fontWeight={600}>
            {vibeTagId ? 'Tracks with This Vibe' : 'Similar Tracks'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!vibeTagId && audioUrl && (
              <IconButton
                aria-label={isSourceTrackPlaying ? 'pause' : 'play'}
                onClick={handlePlaySourceTrack}
                size="small"
                sx={{ color: 'primary.main', p: 0.25 }}
              >
                {isSourceTrackPlaying ? (
                  <PauseCircleIcon fontSize="medium" />
                ) : (
                  <PlayCircleIcon fontSize="medium" />
                )}
              </IconButton>
            )}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={styles.subtitle}
            >
              {vibeTagId ? (
                <>
                  Tracks matching <strong>{trackTitle}</strong> vibe
                </>
              ) : (
                <>
                  Similar to <strong>{trackTitle}</strong> by {trackArtist}
                </>
              )}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={styles.errorContainer}>
            <Typography color="error">{error}</Typography>
            <Button
              variant="outlined"
              onClick={fetchSimilarTracks}
              sx={styles.retryButton}
            >
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && similarTracks.length === 0 && (
          <Typography color="text.secondary" sx={styles.noTracksText}>
            No similar tracks found.
          </Typography>
        )}

        {!loading && !error && similarTracks.length > 0 && (
          <Box sx={styles.tracksContainer}>
            {similarTracks.map((track, index) => {
              const clipId = track.clipId ?? track.trackEmbeddingId;
              const isPlaying =
                !!clipId &&
                playerState.isClipPlaying(clipId) &&
                playerState.playIntent;

              return (
                <TrackCard
                  key={track.trackEmbeddingId}
                  track={track}
                  index={index}
                  isPlaying={isPlaying}
                  previousFeedback={previousFeedback.get(track.trackEmbeddingId)}
                  onPlayPause={() => handlePlayPause(track)}
                  onThumbsUp={() => handleThumbsUp(track)}
                  onThumbsDown={() => handleThumbsDown(track)}
                  onVibeTagClick={(vibeTagId, vibeTagName) =>
                    setVibeTracksModal({ open: true, vibeTagId, vibeTagName })
                  }
                />
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Nested modal for vibe tag tracks */}
      <SimilarTracksModal
        open={vibeTracksModal.open}
        onClose={() =>
          setVibeTracksModal({ open: false, vibeTagId: 0, vibeTagName: '' })
        }
        trackTitle={vibeTracksModal.vibeTagName}
        trackArtist="Vibe Match"
        audioUrl={undefined}
        vibeTagId={vibeTracksModal.vibeTagId}
      />

      {/* Feedback dialogs */}
      <FeedbackReasonDialog
        open={feedbackDialog.open}
        trackTitle={feedbackDialog.trackTitle}
        onClose={() =>
          setFeedbackDialog({ open: false, trackId: 0, trackTitle: '' })
        }
        onSelectReason={handleFeedbackReason}
      />

      <NotSoundReasonDialog
        open={notSoundReasonDialog.open}
        trackTitle={feedbackDialog.trackTitle}
        otherText={notSoundReasonDialog.otherText}
        showOtherInput={notSoundReasonDialog.showOtherInput}
        onClose={() =>
          setNotSoundReasonDialog({
            open: false,
            otherText: '',
            showOtherInput: false,
          })
        }
        onSelectReason={handleNotSoundReason}
        onOtherTextChange={(text) =>
          setNotSoundReasonDialog({
            open: true,
            otherText: text,
            showOtherInput: true,
          })
        }
        onOtherSubmit={handleOtherReasonSubmit}
      />

      <SoundReasonDialog
        open={soundReasonDialog.open}
        trackTitle={feedbackDialog.trackTitle}
        otherText={soundReasonDialog.otherText}
        showOtherInput={soundReasonDialog.showOtherInput}
        onClose={() =>
          setSoundReasonDialog({
            open: false,
            otherText: '',
            showOtherInput: false,
          })
        }
        onSelectReason={handleSoundReason}
        onOtherTextChange={(text) =>
          setSoundReasonDialog({
            open: true,
            otherText: text,
            showOtherInput: true,
          })
        }
        onOtherSubmit={handleSoundOtherReasonSubmit}
      />

      <WhichSongDialog
        open={whichSongDialog.open}
        reason={whichSongDialog.reason}
        originalTrackTitle={trackTitle}
        originalTrackArtist={trackArtist}
        similarTrackTitle={feedbackDialog.trackTitle}
        onClose={() => setWhichSongDialog({ open: false, reason: null })}
        onSelect={handleWhichSong}
      />
    </Dialog>
  );
};
